import React from 'react';
import PropTypes from 'prop-types';
import { isAiBriefAvailable } from '@fiverr-private/ai_brief';
import {
    RecipientInfo,
    AttachmentsUploadConfig,
    SenderInfo,
    BriefInfo,
    VacationInfo
} from '../propTypes';

const StaticContext = React.createContext();

export const StaticContextProvider = ({
    children,
    recipientInfo,
    relatedGigId,
    professionRelatedTranslationKey,
    attachmentsUploadConfig,
    showInfo,
    showSpokenLanguages,
    showCountryFlag,
    showQuickQuestions = true,
    showNightTimeBar = true,
    showSendBatchBrief,
    sendMessageCtaTitle,
    sendMessagePlaceholder,
    initialMessage = '',
    sendMessage,
    pageName,
    source,
    getMoreGigsData,
    offerMoreGigsEnabled = false,
    onViewMessageClick,
    isRequestToOrderModal,
    encourageAppDownloadEnabled = false,
    subCategoryId,
    senderInfo = {},
    inFmpMoreGigsV4Rollout = false,
    brief = {},
    vacation = {},
    withSatisfactionGuarantee = false
}) => (
    <StaticContext.Provider value={{
        recipientInfo,
        relatedGigId,
        professionRelatedTranslationKey,
        attachmentsUploadConfig,
        pageName,
        showInfo,
        showSpokenLanguages,
        showCountryFlag,
        showQuickQuestions,
        showNightTimeBar,
        showSendBatchBrief,
        sendMessageCtaTitle,
        sendMessagePlaceholder,
        initialMessage,
        sendMessage,
        source,
        getMoreGigsData,
        offerMoreGigsEnabled,
        onViewMessageClick,
        isRequestToOrderModal,
        encourageAppDownloadEnabled,
        subCategoryId,
        senderInfo,
        inFmpMoreGigsV4Rollout,
        brief,
        vacation,
        withSatisfactionGuarantee
    }}>
        {children}
    </StaticContext.Provider>
);

StaticContextProvider.propTypes = {
    recipientInfo: RecipientInfo,
    relatedGigId: PropTypes.number,
    subCategoryId: PropTypes.number,
    professionRelatedTranslationKey: PropTypes.string,
    attachmentsUploadConfig: AttachmentsUploadConfig,
    pageName: PropTypes.string,
    source: PropTypes.string,
    showInfo: PropTypes.bool,
    showSpokenLanguages: PropTypes.bool,
    showCountryFlag: PropTypes.bool,
    showQuickQuestions: PropTypes.bool,
    showNightTimeBar: PropTypes.bool,
    showSendBatchBrief: PropTypes.bool,
    sendMessageCtaTitle: PropTypes.string,
    sendMessagePlaceholder: PropTypes.string,
    initialMessage: PropTypes.string,
    sendMessage: PropTypes.func,
    children: PropTypes.node.isRequired,
    getMoreGigsData: PropTypes.func,
    offerMoreGigsEnabled: PropTypes.bool,
    onViewMessageClick: PropTypes.func,
    isRequestToOrderModal: PropTypes.bool,
    encourageAppDownloadEnabled: PropTypes.bool,
    senderInfo: SenderInfo,
    inFmpMoreGigsV4Rollout: PropTypes.bool,
    brief: BriefInfo,
    vacation: VacationInfo,
    withSatisfactionGuarantee: PropTypes.bool
};

export const useStaticContext = () => React.useContext(StaticContext);

export const selectBriefData = (context) => context.brief;
export const shouldShowBrief = (context) => {
    const { sellerId } = context?.recipientInfo || {};
    const { categoryId } = context.brief;
    return categoryId && sellerId && isAiBriefAvailable();
};
export const selectVacationData = (context) => context.vacation;
export const shouldShowVacationMessage = (context) => context.vacation?.endsAt && new Date(context.vacation.endsAt * 1000).valueOf() > new Date().valueOf();

export const selectRecipientInfo = (context) => context.recipientInfo;
export const selectRelatedGigId = (context) => context.relatedGigId;
export const selectProfessionRelatedTranslationKey = (context) => context.professionRelatedTranslationKey;
export const selectSendMessage = (context) => context.sendMessage;
export const selectPageName = (context) => context.pageName;
export const selectEventSource = (context) => context.source;
export const selectShowSpokenLanguages = (context) => context.showSpokenLanguages;
export const selectShowCountryFlag = (context) => context.showCountryFlag;

export const selectShowQuickQuestions = (context) => context.showQuickQuestions;

export const selectShowNightTimeBar = (context) => context.showNightTimeBar;

export const selectSendMessageCtaTitle = (context) => context.sendMessageCtaTitle;

export const selectSendMessagePlaceholder = (context) => context.sendMessagePlaceholder;

export const selectShowSendBatchBrief = (context) => context.showSendBatchBrief;

export const selectInitialMessage = (context) => context.initialMessage;

export const selectSellerName = (context) => {
    const recipientInfo = selectRecipientInfo(context);
    const { displayName, username } = recipientInfo;

    return displayName || username;
};
export const selectViewMessageCallback = (context) => context.onViewMessageClick;
export const selectSubCategoryId = (context) => context.subCategoryId;
export const selectSellerOffersHourlyRate = (context) => context.recipientInfo?.offersHourlyRate;
